import { useState } from 'react';
import { Loader2, Trash2, AlertTriangle, Plus, Edit, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { apiClient } from '../../lib/api-client';
import { buildLeafDeleteRequestBody, postLeafDeleteJson } from '../../lib/leaf-delete-request';
import { buildNewStreamConfig } from './manage-streams.utils';
import { StreamConfigEditDialog } from './StreamConfigEditDialog';
import {
  ReconciliationPreviewDialog,
  type ProposedStep,
  type AuditResourceRow,
} from './ReconciliationPreviewDialog';

interface StreamRow {
  streamId: string;
  displayName: string;
  enabled: boolean;
  productCount: number;
  tenantCount: number;
  repoCount: number;
  workflowTypeCount: number;
}

interface ImpactResource {
  type: string;
  name: string;
  shared: boolean;
  metadata?: { appName?: string; teamName?: string; chatName?: string; channelId?: string; channelKind?: string };
}

interface DeleteImpact {
  rolesToDelete: Array<{
    code: string;
    agentTitle: string;
    resources?: ImpactResource[];
    leafScope?: { product: string; teamType: string; tenantCode: string };
  }>;
  rolesToUpdate: Array<{ code: string; agentTitle: string; refsRemoved: string[] }>;
  sharedResources: Array<{ type: string; name: string; sharedWith: string[] }>;
}

interface ManageStreamsDialogProps {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  streams: StreamRow[];
}

export function ManageStreamsDialog({ open, onClose, onChanged, streams }: ManageStreamsDialogProps) {
  const [newStreamId, setNewStreamId] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [impact, setImpact] = useState<DeleteImpact | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingStreamId, setEditingStreamId] = useState('');
  const [editingYaml, setEditingYaml] = useState('');
  const [editingOpen, setEditingOpen] = useState(false);
  const [editingLoadingId, setEditingLoadingId] = useState<string | null>(null);
  const [auditStreamId, setAuditStreamId] = useState('');
  const [auditSteps, setAuditSteps] = useState<ProposedStep[]>([]);
  const [auditRows, setAuditRows] = useState<AuditResourceRow[]>([]);
  const [auditYaml, setAuditYaml] = useState('');
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState<string | null>(null);
  const [deleteResourceKeys, setDeleteResourceKeys] = useState<Set<string>>(() => new Set());

  const resourceKey = (roleCode: string, r: ImpactResource) =>
    `${roleCode}|${r.type}|${r.name}|${JSON.stringify(r.metadata || {})}`;

  const handleDeleteClick = async (streamId: string) => {
    setError(null);
    setImpact(null);
    setDeleteResourceKeys(new Set());
    setDeletingId(streamId);
    setImpactLoading(true);
    try {
      const result = await apiClient.getAiConfigDeleteImpact(streamId, 'stream', streamId);
      setImpact(result);
      const next = new Set<string>();
      for (const role of result.rolesToDelete || []) {
        for (const r of role.resources || []) {
          if (!r.shared) next.add(resourceKey(role.code, r));
        }
      }
      setDeleteResourceKeys(next);
      setConfirmId(streamId);
    } catch (err: any) {
      setError(err.message || 'Failed to analyze delete impact');
    } finally {
      setImpactLoading(false);
    }
  };

  const handleAddStream = async () => {
    const streamId = newStreamId.trim();
    if (!streamId) return;

    setError(null);
    setAdding(true);
    try {
      const payload = buildNewStreamConfig(streamId, newDisplayName);
      await apiClient.createAiConfigStream(streamId, JSON.stringify(payload, null, 2));
      setNewStreamId('');
      setNewDisplayName('');
      onChanged();
    } catch (err: any) {
      setError(err.message || 'Failed to create stream');
    } finally {
      setAdding(false);
    }
  };

  const handleEditStream = async (streamId: string) => {
    setError(null);
    setEditingLoadingId(streamId);
    setEditingStreamId(streamId);
    setEditingYaml('');
    setEditingOpen(false);
    try {
      const res = await apiClient.getAiConfigStreamYaml(streamId);
      setEditingYaml(res.yaml);
      setEditingOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load stream config');
    } finally {
      setEditingLoadingId(null);
    }
  };

  const handleReconcile = async (streamId: string) => {
    setError(null);
    setAuditLoading(streamId);
    try {
      const [auditRes, catchUpRes, yamlRes] = await Promise.all([
        apiClient.auditReconciliation(streamId),
        apiClient.previewStreamCatchUpReconciliation(streamId),
        apiClient.getAiConfigStreamYaml(streamId),
      ]);
      setAuditStreamId(streamId);
      setAuditRows((auditRes.discrepancies || []) as AuditResourceRow[]);
      setAuditSteps((catchUpRes.proposedSteps || []) as ProposedStep[]);
      setAuditYaml(yamlRes.yaml || '');
      setAuditOpen(true);
    } catch (err: any) {
      setError(err.message || 'Audit failed');
    } finally {
      setAuditLoading(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmId || !impact) return;
    setDeleting(true);
    setError(null);
    try {
      const streamId = confirmId;
      const apiBase = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3000';

      for (const role of impact.rolesToDelete) {
        const scope = role.leafScope;
        const resources = (role.resources || []).filter((r) => deleteResourceKeys.has(resourceKey(role.code, r)));
        if (!scope || resources.length === 0) continue;
        const body = buildLeafDeleteRequestBody(resources);
        try {
          await postLeafDeleteJson(apiBase, streamId, scope.product, scope.teamType, scope.tenantCode, body);
        } catch (e: any) {
          throw new Error(e?.message || `Leaf delete failed for role ${role.code}`);
        }
      }

      await apiClient.deleteAiConfigStream(streamId);
      setConfirmId(null);
      setDeletingId(null);
      setImpact(null);
      setDeleteResourceKeys(new Set());
      onChanged();
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setConfirmId(null);
    setDeletingId(null);
    setImpact(null);
    setDeleteResourceKeys(new Set());
    setError(null);
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent maxWidth="3xl">
        <DialogHeader>
          <DialogTitle>Manage Streams</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <th className="pb-2 pr-3">Name</th>
                  <th className="pb-2 pr-3">Status</th>
                  <th className="pb-2 pr-3 text-right">Products</th>
                  <th className="pb-2 pr-3 text-right">Tenants</th>
                  <th className="pb-2 pr-3 text-right">Repos</th>
                  <th className="pb-2 pr-3 text-right">WF Types</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {streams.map((s) => (
                  <tr key={s.streamId} className="text-gray-800 dark:text-gray-200">
                    <td className="py-2.5 pr-3 font-medium">{s.displayName || s.streamId}</td>
                    <td className="py-2.5 pr-3">
                      <Badge variant={s.enabled ? 'success' : 'warning'}>
                        {s.enabled ? 'Active' : 'Paused'}
                      </Badge>
                    </td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{s.productCount}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{s.tenantCount}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{s.repoCount}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{s.workflowTypeCount}</td>
                    <td className="py-2.5 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditStream(s.streamId)}
                        disabled={editingLoadingId === s.streamId}
                      >
                        {editingLoadingId === s.streamId ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Edit className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReconcile(s.streamId)}
                        disabled={auditLoading === s.streamId}
                        title="Reconcile external resources"
                      >
                        {auditLoading === s.streamId ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                        onClick={() => handleDeleteClick(s.streamId)}
                        disabled={deletingId === s.streamId && impactLoading}
                      >
                        {deletingId === s.streamId && impactLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
                {streams.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-gray-500 dark:text-gray-400">No streams configured</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Add Stream</p>
            <div className="flex gap-2">
              <Input
                placeholder="Stream ID"
                value={newStreamId}
                onChange={(e) => setNewStreamId(e.target.value)}
                className="text-sm"
              />
              <Input
                placeholder="Display name (optional)"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                className="text-sm"
              />
              <Button size="sm" onClick={handleAddStream} disabled={adding || !newStreamId.trim()}>
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {confirmId && impact && (
            <div className="mt-4 p-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                <span className="font-semibold text-amber-800 dark:text-amber-200">
                  Delete &ldquo;{streams.find((s) => s.streamId === confirmId)?.displayName || confirmId}&rdquo;?
                </span>
              </div>

              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                The stream and all roles will be removed from configuration. Select external resources to deprovision first; shared resources default unchecked.
              </p>

              {impact.rolesToDelete.length > 0 && (
                <div className="mb-2 space-y-3 max-h-64 overflow-y-auto">
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Roles and external resources</p>
                  {impact.rolesToDelete.map((r) => (
                    <div key={r.code} className="border border-amber-200/80 dark:border-amber-800/50 rounded-md p-2 bg-white/60 dark:bg-gray-900/40">
                      <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                        {r.agentTitle} <span className="font-mono text-gray-500">({r.code})</span>
                      </p>
                      {(r.resources || []).length === 0 ? (
                        <p className="text-[11px] text-gray-500 mt-1">No mapped leaf resources (check product/team/tenant on role).</p>
                      ) : (
                        <ul className="mt-1.5 space-y-1">
                          {(r.resources || []).map((res) => {
                            const key = resourceKey(r.code, res);
                            return (
                              <li key={key} className="flex items-start gap-2 text-[11px]">
                                <input
                                  type="checkbox"
                                  className="mt-0.5 rounded border-gray-300"
                                  checked={deleteResourceKeys.has(key)}
                                  disabled={deleting}
                                  onChange={() => {
                                    setDeleteResourceKeys((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(key)) next.delete(key);
                                      else next.add(key);
                                      return next;
                                    });
                                  }}
                                />
                                <span className="text-gray-700 dark:text-gray-300">
                                  <span className="font-mono">{res.type}</span> {res.name}
                                  {res.shared ? <span className="text-amber-700 dark:text-amber-400"> (shared)</span> : null}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {impact.sharedResources.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Shared resources:</p>
                  <ul className="text-xs text-gray-600 dark:text-gray-400 list-disc list-inside">
                    {impact.sharedResources.map((r, i) => (
                      <li key={i}>{r.type}: {r.name} (shared with {r.sharedWith.join(', ')})</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex gap-2 mt-3">
                <Button variant="destructive" size="sm" onClick={handleConfirmDelete} disabled={deleting}>
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Confirm Delete
                </Button>
                <Button variant="outline" size="sm" onClick={handleCancelDelete} disabled={deleting}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
      {editingOpen && editingStreamId && (
        <StreamConfigEditDialog
          open={editingOpen}
          onClose={() => setEditingOpen(false)}
          streamId={editingStreamId}
          initialYaml={editingYaml}
          onSaved={() => {
            setEditingOpen(false);
            onChanged();
          }}
        />
      )}
      {auditOpen && auditStreamId && (
        <ReconciliationPreviewDialog
          open={auditOpen}
          onClose={() => setAuditOpen(false)}
          proposedSteps={auditSteps}
          mode="audit"
          auditRows={auditRows}
          executePayload={{
            streamId: auditStreamId,
            entityType: 'stream',
            entityId: auditStreamId,
            yaml: auditYaml,
          }}
          onExecuted={() => {
            setAuditOpen(false);
            onChanged();
          }}
        />
      )}
    </Dialog>
  );
}
